/* LaborRatioPanel — the "FTA Ratio" section inside the Live Labor Cost
   Monitor. Tracks weekly full-time-employees (FTA) ÷ occupied-rooms and
   raises an ALARM when the ratio is strictly greater than the configured
   threshold. Manual weekly entry; admins/engineers edit, everyone reads. */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, AlertTriangle, CheckCircle2, Loader2, Users } from 'lucide-react';
import { isAdminLike } from '../lib/permissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pct = (r) => `${(Number(r || 0) * 100).toFixed(1)}%`;

export default function LaborRatioPanel({ settings }) {
  const [data, setData] = useState({ entries: [], threshold: 0.15, latest: null, alarm_count: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ week_start: '', fta: '', occupied_rooms: '' });
  const [saving, setSaving] = useState(false);
  const [thr, setThr] = useState('');
  const [savingThr, setSavingThr] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const canEdit = isAdminLike(typeof window !== 'undefined' ? localStorage.getItem('user_role') : null);
  const auth = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  const primary = settings?.primary_color || '#1F4FBE';

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/labor-ratio`, auth);
      setData(d);
      setThr(String(d.threshold));
    } catch { /* keep */ } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const saveEntry = async () => {
    if (!form.week_start) { toast.error('Pick the week (any date in that week).'); return; }
    if (form.fta === '' || form.occupied_rooms === '') { toast.error('Enter FTA and occupied rooms.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/labor-ratio/entries`, {
        week_start: form.week_start, fta: Number(form.fta), occupied_rooms: Number(form.occupied_rooms),
      }, auth);
      toast.success('Weekly entry saved.');
      setForm({ week_start: '', fta: '', occupied_rooms: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save entry.');
    } finally { setSaving(false); }
  };

  const removeEntry = async (id) => {
    try { await axios.delete(`${API}/admin/labor-ratio/entries/${id}`, auth); load(); }
    catch { toast.error('Could not delete entry.'); }
  };

  const saveThreshold = async () => {
    setSavingThr(true);
    try {
      await axios.put(`${API}/admin/labor-ratio/config`, { threshold: Number(thr) }, auth);
      toast.success('Threshold updated.');
      load();
    } catch { toast.error('Could not update threshold.'); }
    finally { setSavingThr(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-full text-white/60"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>;

  const latest = data.latest;
  const alarm = latest?.alarm;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="labor-ratio-panel">
      {/* Latest ratio hero */}
      <div className={`rounded-xl p-5 ${alarm ? 'labor-glow-red' : ''}`}
        style={{ backgroundColor: alarm ? '#3B0D0D' : 'rgba(255,255,255,.06)', border: `1px solid ${alarm ? '#EF4444' : 'rgba(255,255,255,.12)'}` }}
        data-testid="labor-ratio-latest">
        <div className="flex items-center gap-2 mb-1">
          <Users className="w-4 h-4 text-white/60" />
          <p className="text-white/60 text-xs font-bold uppercase tracking-wider">FTA ÷ Occupied Rooms {latest ? `· week of ${latest.week_start}` : ''}</p>
        </div>
        {latest ? (
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <p className="text-4xl font-extrabold" style={{ color: alarm ? '#FCA5A5' : '#fff' }} data-testid="labor-ratio-value">{pct(latest.ratio)}</p>
              <p className="text-xs text-white/50 mt-1">{latest.fta} FTA · {latest.occupied_rooms} rooms · threshold {pct(data.threshold)}</p>
            </div>
            <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${alarm ? 'bg-red-500 text-white' : 'bg-emerald-500/20 text-emerald-300'}`} data-testid="labor-ratio-status">
              {alarm ? <><AlertTriangle className="w-4 h-4" /> ALARM</> : <><CheckCircle2 className="w-4 h-4" /> OK</>}
            </span>
          </div>
        ) : (
          <p className="text-white/50 text-sm mt-1">No weekly entries yet.{canEdit ? ' Add one below.' : ''}</p>
        )}
      </div>

      {/* Threshold config (admin) */}
      {canEdit && (
        <div className="rounded-xl p-4 bg-white/5 border border-white/10">
          <p className="text-white/80 font-bold text-sm mb-2">Alarm threshold (ratio)</p>
          <div className="flex items-center gap-2">
            <input type="number" step="0.01" min="0" value={thr} onChange={(e) => setThr(e.target.value)} data-testid="labor-ratio-threshold-input"
              className="w-32 rounded-lg px-3 py-2 text-sm" />
            <span className="text-white/50 text-xs">Alarm fires when a week&apos;s ratio is strictly greater than this.</span>
            <button onClick={saveThreshold} disabled={savingThr} className="ml-auto px-4 py-2 rounded-lg text-white font-bold text-sm disabled:opacity-50" style={{ backgroundColor: primary }} data-testid="labor-ratio-threshold-save">
              {savingThr ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Add weekly entry (admin) */}
      {canEdit && (
        <div className="rounded-xl p-4 bg-white/5 border border-white/10" data-testid="labor-ratio-add-form">
          <p className="text-white/80 font-bold text-sm mb-3">Add / update weekly entry</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[11px] text-white/50 font-bold uppercase tracking-wider">Week</span>
              <input type="date" value={form.week_start} onChange={(e) => setForm((f) => ({ ...f, week_start: e.target.value }))} data-testid="labor-ratio-week" className="mt-1 w-full rounded-lg px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[11px] text-white/50 font-bold uppercase tracking-wider">FTA (full-time)</span>
              <input type="number" min="0" value={form.fta} onChange={(e) => setForm((f) => ({ ...f, fta: e.target.value }))} placeholder="e.g. 20" data-testid="labor-ratio-fta" className="mt-1 w-full rounded-lg px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-[11px] text-white/50 font-bold uppercase tracking-wider">Occupied rooms</span>
              <input type="number" min="0" value={form.occupied_rooms} onChange={(e) => setForm((f) => ({ ...f, occupied_rooms: e.target.value }))} placeholder="e.g. 100" data-testid="labor-ratio-rooms" className="mt-1 w-full rounded-lg px-3 py-2 text-sm" />
            </label>
          </div>
          <button onClick={saveEntry} disabled={saving} className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-bold text-sm disabled:opacity-50" style={{ backgroundColor: primary }} data-testid="labor-ratio-save">
            <Plus className="w-4 h-4" /> {saving ? 'Saving…' : 'Save weekly entry'}
          </button>
        </div>
      )}

      {/* History */}
      <div className="rounded-xl overflow-hidden border border-white/10" data-testid="labor-ratio-history">
        <div className="px-4 py-2.5 bg-white/5 flex items-center justify-between">
          <p className="text-white/80 font-bold text-sm">Weekly history</p>
          {data.alarm_count > 0 && <span className="text-[11px] font-bold text-red-300">{data.alarm_count} week(s) in alarm</span>}
        </div>
        {data.entries.length === 0 ? (
          <p className="px-4 py-6 text-center text-white/40 text-sm">No entries recorded yet.</p>
        ) : (
          <ul>
            {data.entries.map((e) => (
              <li key={e.id} data-testid={`labor-ratio-row-${e.id}`} className="flex items-center gap-3 px-4 py-3 border-t border-white/5">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold">Week of {e.week_start}</p>
                  <p className="text-white/50 text-xs">{e.fta} FTA · {e.occupied_rooms} rooms</p>
                </div>
                <p className="text-lg font-extrabold" style={{ color: e.alarm ? '#FCA5A5' : '#A3D4D4' }}>{pct(e.ratio)}</p>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${e.alarm ? 'bg-red-500 text-white' : 'bg-emerald-500/20 text-emerald-300'}`}>{e.status}</span>
                {canEdit && (
                  <button onClick={() => removeEntry(e.id)} className="p-1 text-white/40 hover:text-red-400" title="Delete" data-testid={`labor-ratio-delete-${e.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
